/**
 * theme-init.js — LabSafetyShop Theme
 * Last in the §6.1 defer chain — runs after Alpine, Lenis, GSAP, ScrollTrigger are available.
 */

(function () {
  'use strict';

  // ── 1. Remove no-js, add js ──────────────────────────────────────────────
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  // ── 2. GSAP plugin registration ──────────────────────────────────────────
  if (typeof gsap !== 'undefined') {
    gsap.defaults({ ease: 'power3.out', duration: 0.65 });

    var plugins = [ScrollTrigger, SplitText, CustomEase, Observer].filter(Boolean);
    if (plugins.length) gsap.registerPlugin.apply(gsap, plugins);

    if (typeof CustomEase !== 'undefined') {
      CustomEase.create('labsafe', 'M0,0 C0.2,0 0.05,1 1,1');
    }
  }

  // ── 2b. ScrollTrigger reliability safety net ─────────────────────────────
  //   Problem: dozens of sections gate entrance animations (icons, stat
  //   cards, value cards, etc.) behind `gsap.fromTo(el, from, { ...,
  //   scrollTrigger: { once: true } })`. gsap.fromTo() applies the hidden
  //   "from" state synchronously, then only reaches the visible "to" state
  //   once the ScrollTrigger fires. Two contexts can leave elements stuck
  //   at the hidden state permanently:
  //     (a) Shopify Theme Editor preview iframe — ScrollTrigger's scroll
  //         listener/viewport math doesn't always fire reliably in that
  //         embedded context, so `once: true` triggers can simply never
  //         reach "once". This is why icons/cards can render fine on the
  //         live storefront but stay invisible in the editor.
  //     (b) Real visitors on a slow/janky load — a trigger's start position
  //         gets computed before web fonts/images finish shifting layout.
  //   Fix, applied globally at the one place all ScrollTrigger-linked
  //   tweens pass through (ScrollTrigger.create, which gsap.fromTo/from
  //   call internally when given a `scrollTrigger` option):
  //     (a) in the editor, force every one-shot (once:true, non-scrub)
  //         trigger to resolve immediately so nothing is ever left hidden;
  //     (b) on the live site, refresh ScrollTrigger once after `load` so
  //         any mistimed trigger recalculates against final layout.
  //   Scoped to once:true + !scrub so continuous scrub/parallax effects
  //   (e.g. sections/article.liquid) are untouched.
  if (typeof ScrollTrigger !== 'undefined') {
    var DESIGN_MODE = !!(window.Shopify && Shopify.designMode);
    var _stCreate = ScrollTrigger.create;
    ScrollTrigger.create = function (vars) {
      var st = _stCreate.call(ScrollTrigger, vars);
      if (DESIGN_MODE && vars && vars.once && !vars.scrub) {
        if (st.animation) {
          st.animation.progress(1);
        } else if (typeof vars.onEnter === 'function') {
          try { vars.onEnter.call(st, st); } catch (e) {}
        }
      }
      return st;
    };

    window.addEventListener('load', function () {
      setTimeout(function () { ScrollTrigger.refresh(); }, 250);
    });
  }

  // ── 3. Lenis smooth scroll — synced to GSAP ticker ─────────────────────
  if (typeof Lenis !== 'undefined') {
    window.__lenis = new Lenis({
      duration: 1.15,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothTouch: false,
      touchMultiplier: 2,
      // allowNestedScroll: lets wheel/touch events over an internally
      // scrollable panel (e.g. the expanded collection description,
      // .lss-col-desc__scroll) scroll natively *inside* that panel while
      // it has room left, then automatically hand control back to Lenis
      // for the main page scroll the instant the panel hits its top/bottom
      // edge — no more dead-ending once the inner scrollbar reaches its end.
      allowNestedScroll: true,
    });

    if (typeof gsap !== 'undefined') {
      if (typeof ScrollTrigger !== 'undefined') {
        window.__lenis.on('scroll', ScrollTrigger.update);
      }
      gsap.ticker.add(function (time) { window.__lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      (function raf(time) { window.__lenis.raf(time); requestAnimationFrame(raf); })(0);
    }
  }

  // ── 4. NProgress nav progress bar ───────────────────────────────────────
  if (typeof NProgress !== 'undefined') {
    NProgress.configure({ showSpinner: false, minimum: 0.15, speed: 300, trickleSpeed: 200 });
    var style = document.createElement('style');
    style.textContent = '#nprogress .bar{background:#ffff19!important}#nprogress .peg{box-shadow:0 0 10px #ffff19,0 0 5px #ffff19!important}';
    document.head.appendChild(style);
  }

  // ── 5. Global keyboard shortcuts ─────────────────────────────────────────
  if (typeof hotkeys !== 'undefined') {
    hotkeys('/', function (e) {
      var input = document.querySelector('[data-search-input]');
      if (input && document.activeElement !== input) { e.preventDefault(); input.focus(); }
    });
    hotkeys('esc', function () {
      document.dispatchEvent(new CustomEvent('labsafe:close-all'));
    });
  }

  // ── 6. GSAP page entrance animations ─────────────────────────────────────
  //    Runs once on load; all hero cards + carousel content reveal in sequence.
  //    Subsequent scroll-reveal handled by ScrollTrigger on [data-gsap-reveal].
  if (typeof gsap !== 'undefined') {

    // — 6a. Hero carousel content ————————————————————————————————————————
    // NOTE: Content uses CSS `hero-content-in` class for guaranteed visibility.
    // GSAP enhances with a subtle x-slide if available; CSS covers the fallback.
    var heroContent = document.querySelector('[data-gsap-hero-content]');
    if (heroContent) {
      gsap.fromTo(heroContent,
        { x: -18 },                          // only animate X — CSS handles opacity
        { x: 0, duration: 0.65, delay: 0.1, ease: 'power3.out', clearProps: 'x' }
      );
    }

    // — 6b. Hero right-panel cards — GSAP overrides CSS animation ————————
    // CSS stagger runs immediately; GSAP replaces if loaded (smoother).
    var heroCards = document.querySelectorAll('[data-gsap-hero-card]');
    if (heroCards.length) {
      // Remove CSS animation first so GSAP fully controls the sequence
      heroCards.forEach(function(c) {
        c.style.animation = 'none';
        c.style.opacity = '0';
        c.style.transform = 'translateY(36px)';
      });
      gsap.to(heroCards, {
        opacity: 1,
        y: 0,
        stagger: 0.11,
        duration: 0.65,
        delay: 0.28,
        ease: 'power3.out',
        clearProps: 'transform,opacity',
      });
    }

    // — 6c. Trust bar stats — entrance fade is pure CSS now (see
    //        sections/trust-bar.liquid). Two independent gsap.from() calls
    //        (this one + trust-bar.liquid's) used to race on the same
    //        elements and could leave stats stuck invisible; removed here
    //        in favor of the single CSS-only reveal.

    // — 6d. Generic scroll-reveal for any element with [data-gsap-reveal] ——
    if (typeof ScrollTrigger !== 'undefined') {
      document.querySelectorAll('[data-gsap-reveal]').forEach(function (el) {
        var delay = parseFloat(el.dataset.gsapDelay || 0);
        var dir   = el.dataset.gsapDir || 'up'; // up | left | right | none
        var fromVars = { opacity: 0, duration: 0.6, delay: delay, ease: 'power3.out' };
        if (dir === 'up')    fromVars.y  =  28;
        if (dir === 'left')  fromVars.x  = -28;
        if (dir === 'right') fromVars.x  =  28;
        gsap.from(el, Object.assign(fromVars, {
          scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        }));
      });
    }

    // — 6e. Category cards — stagger reveal on scroll ————————————————————
    // NOTE: Only animate y (translateY), NOT opacity.
    // Alpine's :class binding owns opacity (opacity-100 / opacity-55 hover system).
    // If GSAP also sets inline opacity:0, it wins over Tailwind classes and causes
    // cards to appear invisible until the tween completes — a visible flash on load.
    // Animating y-only gives a clean entrance without touching Alpine's opacity state.
    var catSection = document.querySelector('#shop-by-category');
    if (catSection && typeof ScrollTrigger !== 'undefined') {
      gsap.from(catSection.querySelectorAll('.cat-card'), {
        y: 32,
        stagger: 0.09,
        duration: 0.55,
        ease: 'power3.out',
        clearProps: 'transform',
        scrollTrigger: {
          trigger: catSection,
          start: 'top 88%',
          once: true,
        },
      });
    }

  }
  // ── End GSAP animations ──────────────────────────────────────────────────

})();
